import {Formik} from 'formik';
import {compose} from 'redux';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import {withRouter} from 'react-router';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import {connect, useSelector} from 'react-redux';
import ClearIcon from '@material-ui/icons/Clear';
import {FormattedMessage, useIntl} from 'react-intl';
import DialogTitle from '@material-ui/core/DialogTitle';
import React, {useEffect, useRef, useState} from 'react';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';

import validationSchema from './ValidationSchema';
import {toast} from '../../base-components/StudioToast';
import {PROJECT_SETTINGS} from './../../config/constants';
import {FileExplorer} from '../TestDeployView/FileExplorer';
import {DropArea} from '../../base-components/StudioDropArea';
import {useStudioMode} from '../StudioMode/StudioModeProvider';
import Waiting from '../../base-components/StudioWaiting/Waiting';
import {StudioTextField} from './../../base-components/StudioTextField';
import {
  projectCreate,
  projectUpload,
  getIsProjectPending,
  getProjectPendingStatus,
} from '../../store/project';
import {
  getViewProjectForm,
  viewCloseProjectForm,
  getIsNewProjectFormOpen,
  getProjectFormType,
} from '../../store/view';

import './ProjectForm.scss';

function ProjectForm({
  open,
  onClose,
  isPending,
  onSubmit,
  dataset,
  model,
  isp,
  pp,
  solution,
  status,
  onProjectUpload,
  modalType,
}) {
  const intl = useIntl();
  const {mode} = useStudioMode();
  const serverConfig = useSelector(state => state.config);
  const isLiteMode = mode === 'LITE';
  const resetAfterSubmit = useRef(null);
  const prevIsPending = useRef(isPending);

  const [files, setFiles] = useState([]);
  const [selectedModalType, setSelectedModalType] = useState(modalType || 'create');

  useEffect(() => {
    if (open && prevIsPending.current === true && !isPending) {
      resetAfterSubmit.current?.();
    }
    prevIsPending.current = isPending;
  }, [isPending, onClose, open]);

  useEffect(() => {
    if (isLiteMode && open && solution) {
      resetAfterSubmit.current = () => {
        onClose();
        setFiles([]);
      };
      onSubmit({
        description: solution.description,
        name: solution.displayName,
        solution,
        mode: 'LITE',
      });
    }
  }, [isLiteMode, open, solution, onSubmit, onClose]);

  // using this to reset the files
  useEffect(() => {
    setFiles([]);
  }, [open]);

  useEffect(() => {
    if (files && files.length) {
      let total = 0;
      files.forEach(({size: fSize}) => (total += fSize));
      total /= 1000000;
      if (total > serverConfig?.maxRequestSize) {
        toast.warn({
          subtitle: intl.formatMessage(
            {id: 'importDialog.warn.upload.limit'},
            {limit: serverConfig?.maxRequestSize + 'mb'}
          ),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, serverConfig?.maxRequestSize, intl]);

  useEffect(() => {
    setSelectedModalType(modalType);
  }, [modalType]);

  const handleFileListChange = newFiles => setFiles([newFiles[newFiles.length - 1]]);

  const handleFileListDelete = fileIndex =>
    setFiles(files.filter((file, index) => index !== fileIndex));

  const submitUploadProject = async () => {
    await onProjectUpload(files);
  };

  return (
    <div>
      <Formik
        validateOnChange={true}
        initialValues={{
          name: solution?.displayName,
          description: solution?.description,
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={async (data, {resetForm}) => {
          resetAfterSubmit.current = () => {
            resetForm();
            onClose();
          };
          onSubmit({
            ...validationSchema.cast(data),
            solution,
            dataset,
            model,
            isp,
            pp,
          });
        }}
      >
        {({
          dirty,
          errors,
          handleChange,
          values: {name, description},
          handleBlur,
          handleSubmit,
          handleReset,
        }) => (
          <React.Fragment>
            <Dialog
              open={open}
              onClose={() => {
                handleReset();
                onClose();
                setFiles([]);
              }}
              aria-labelledby="form-dialog-title"
              maxWidth="sm"
              fullWidth
              className="project-form-container"
              data-testid="create-project-form"
              id="create-project-form"
            >
              {selectedModalType === 'create' ? (
                <>
                  <DialogTitle
                    disableTypography={true}
                    className="project-form-container__header"
                  >
                    <h4 className="project-form-container__header__title">
                      <FormattedMessage tagName="span" id="projectExplorer.create" />
                    </h4>

                    <ClearIcon
                      className="project-form-container__header__close"
                      onClick={() => {
                        handleReset();
                        onClose();
                      }}
                    />
                  </DialogTitle>
                  <DialogContent className="content">
                    <Box component="div" m={2}>
                      <form onSubmit={handleSubmit} noValidate autoComplete="off">
                        <StudioTextField
                          inputProps={{maxLength: PROJECT_SETTINGS.name.maxLength}}
                          placeholder={intl.formatMessage({
                            id: 'projectPanel.namePlaceholder',
                          })}
                          name="name"
                          autoFocus
                          margin="normal"
                          id="name"
                          label={intl.formatMessage({
                            id: isLiteMode
                              ? 'studioLite.solutionForm.name'
                              : 'projectPanel.nameLabel',
                          })}
                          required
                          fullWidth
                          className="name-field"
                          disabled={isPending || isLiteMode}
                          data-testid="create-project-page-name"
                          useFieldProps={false}
                          value={name}
                          strategy="formik"
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <StudioTextField
                          inputProps={{
                            className: PROJECT_SETTINGS.description.className,
                            maxLength: PROJECT_SETTINGS.description.maxLength,
                          }}
                          placeholder={intl.formatMessage({
                            id: 'projectPanel.descriptionPlaceholder',
                          })}
                          name="description"
                          margin="normal"
                          variant="filled"
                          id="description"
                          label={intl.formatMessage({
                            id: 'projectPanel.descriptionLabel',
                          })}
                          fullWidth
                          multiline={PROJECT_SETTINGS.description.multiline}
                          disabled={isPending || isLiteMode}
                          data-testid="create-project-page-description"
                          useFieldProps={false}
                          value={description}
                          strategy="formik"
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </form>
                      {isPending && (
                        <Waiting
                          diameter={40}
                          loadingText={
                            status || intl.formatMessage({id: 'project.loading'})
                          }
                          className="project-form-container__loading"
                        />
                      )}
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Box className="project-form-container__footer">
                      <Button
                        onClick={() => setSelectedModalType('upload')}
                        buttonRole="secondary"
                        size="large"
                        variant="outlined"
                      >
                        <FormattedMessage id="form.uploadProject" />
                      </Button>

                      <Box className="project-form-container__footer__actions">
                        <Button
                          onClick={() => {
                            handleReset();
                            setFiles([]);
                            onClose();
                          }}
                          buttonRole="secondary"
                          size="large"
                          variant="outlined"
                        >
                          <FormattedMessage id="form.cancel" />
                        </Button>
                        {!isLiteMode && (
                          <Button
                            onClick={handleSubmit}
                            color="primary"
                            size="large"
                            variant="contained"
                            type="submit"
                            disabled={isPending || !!Object.keys(errors).length}
                            data-testid="create-project-page-submit"
                            id="create-project-page-submit"
                          >
                            <FormattedMessage id="form.createProject" />
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </DialogActions>
                </>
              ) : selectedModalType === 'upload' ? (
                <>
                  <DialogTitle
                    disableTypography={true}
                    className="project-form-container__header"
                  >
                    <h4 className="project-form-container__header__title">
                      <FormattedMessage tagName="span" id="projectExplorer.uploadFiles" />
                    </h4>

                    <ClearIcon
                      className="project-form-container__header__close"
                      onClick={() => {
                        handleReset();
                        setFiles([]);
                        onClose();
                      }}
                    />
                  </DialogTitle>
                  <DialogContent className="content">
                    <DropArea
                      size="xlg"
                      onChange={handleFileListChange}
                      className="import-dialog__drop"
                    />
                    <FileExplorer
                      files={files}
                      onDelete={(file, index) => handleFileListDelete(index)}
                      onClear={() => setFiles([])}
                      size="small"
                      icon="delete"
                      className="import-dialog__files"
                    />

                    {isPending && (
                      <Waiting
                        diameter={40}
                        loadingText={
                          status || intl.formatMessage({id: 'project.loading'})
                        }
                        className="project-form-container__loading"
                      />
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Box className="project-form-container__footer">
                      <Button
                        onClick={() => setSelectedModalType('create')}
                        buttonRole="secondary"
                        size="large"
                        variant="outlined"
                      >
                        <FormattedMessage id="form.createProject" />
                      </Button>

                      <Box className="project-form-container__footer__actions">
                        <Button
                          onClick={() => {
                            setFiles([]);
                            handleReset();
                            onClose();
                          }}
                          buttonRole="secondary"
                          size="large"
                          variant="outlined"
                        >
                          <FormattedMessage id="form.cancel" />
                        </Button>
                        <Button
                          onClick={submitUploadProject}
                          color="primary"
                          size="large"
                          variant="contained"
                          type="submit"
                          disabled={files.length <= 0}
                          data-testid="create-project-page-submit"
                          id="create-project-page-submit"
                        >
                          <FormattedMessage id="form.uploadProject" />
                        </Button>
                      </Box>
                    </Box>
                  </DialogActions>
                </>
              ) : null}
            </Dialog>
          </React.Fragment>
        )}
      </Formik>
    </div>
  );
}

ProjectForm.propTypes = {
  open: PropTypes.bool.isRequired,
  isPending: PropTypes.bool.isRequired,
  templateId: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  status: PropTypes.string,
  onProjectUpload: PropTypes.func,
};

ProjectForm.defaultProps = {
  templateId: null,
  onSubmit: () => {},
  onClose: () => {},
  onProjectUpload: () => {},
  status: null,
};

const mapStateToProps = state => ({
  open: getIsNewProjectFormOpen(state),
  dataset: getViewProjectForm(state).dataset,
  model: getViewProjectForm(state).model,
  isp: getViewProjectForm(state).isp,
  pp: getViewProjectForm(state).pp,
  solution: getViewProjectForm(state).solution,
  isPending: getIsProjectPending(state),
  status: getProjectPendingStatus(state),
  modalType: getProjectFormType(state),
});

const mapDispatchToProps = {
  onSubmit: projectCreate,
  onClose: viewCloseProjectForm,
  onProjectUpload: projectUpload,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter
)(ProjectForm);
